import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { toast } from 'react-toastify';
import { API } from '../../../api';
import { formatClock } from '../duration';
import { pillButtonSx, vb } from '../theme';
import { Allowance, MODE_LABEL, MODE_NOUN, RenderJob, VideoProject, isTerminalJob } from '../types';
import { apiCode, apiMessage } from '../useAutosave';

type Phase = 'requesting' | 'rendering' | 'ready' | 'failed' | 'publishing' | 'saved';

const STAGES: Array<{
  key: string;
  label: string;
  done: (j: RenderJob | null) => boolean;
  active: (j: RenderJob | null) => boolean;
}> = [
  {
    key: 'prep',
    label: 'Preparing narration',
    done: (j) => Boolean(j && j.progress >= 10),
    active: (j) => Boolean(j && j.progress < 10)
  },
  {
    key: 'speech',
    label: 'Generating speech',
    done: (j) => Boolean(j && j.progress >= 30),
    active: (j) => Boolean(j && j.progress >= 10 && j.progress < 30)
  },
  {
    key: 'render',
    label: 'Rendering video',
    done: (j) => Boolean(j && j.progress >= 85),
    active: (j) => Boolean(j && j.progress >= 30 && j.progress < 85)
  },
  {
    key: 'upload',
    label: 'Uploading',
    done: (j) => Boolean(j && j.status === 'complete'),
    active: (j) => Boolean(j && j.progress >= 85 && j.status !== 'complete')
  }
];

/**
 * A render that never reports a terminal status must still end somewhere.
 *
 * The backend fails an abandoned job on its own — still queued after 10
 * minutes, or running with no progress write for 35 (RENDER_QUEUED_STALE_MS /
 * RENDER_RUNNING_STALE_MS) — and this poller reads that outcome like any
 * other. This deadline sits past that window as the backstop for when the
 * answer never arrives at all: a status this client doesn't treat as terminal,
 * or a job the backend never reconciles. Without it the poll below ran
 * forever behind a spinner with no way out.
 */
const POLL_DEADLINE_MS = 40 * 60 * 1000;

/**
 * Single failed polls are normal across a render this long — a dropped
 * connection, a cold function, a navigation. A full minute of unbroken
 * failures is not, and the user should be told instead of left watching an
 * indicator that can no longer change.
 */
const POLL_ERROR_GRACE_MS = 60 * 1000;

// Preview + save (§13, §46, §55): request a preview render, show stage
// progress, play it, then publish (final render) and attach to the lesson.
const PreviewStep: React.FC<{
  project: VideoProject;
  lessonName: string;
  allowance: Allowance;
  onProject: (p: VideoProject) => void;
  onKeepEditing: () => void;
  onBackground: () => void;
  onSaved: () => void;
}> = ({ project, lessonName, allowance, onProject, onKeepEditing, onBackground, onSaved }) => {
  const needsRender = !project.previewUrl || project.previewStale;
  const [phase, setPhase] = useState<Phase>(
    project.activeRenderJobId ? 'rendering' : needsRender ? 'requesting' : 'ready'
  );
  const [job, setJob] = useState<RenderJob | null>(null);
  const [jobId, setJobId] = useState<string | null>(project.activeRenderJobId);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(
    () => () => {
      alive.current = false;
    },
    []
  );

  const request = useCallback(
    async (kind: 'preview' | 'final') => {
      // Drop the previous job BEFORE switching phase: the poller must never
      // read the old (already complete) job as this request's outcome.
      setError(null);
      setJob(null);
      setJobId(null);
      setPhase(kind === 'preview' ? 'rendering' : 'publishing');
      try {
        const res: any =
          kind === 'preview'
            ? await API.requestVideoPreview({ projectId: project.id })
            : await API.requestVideoPublish({ projectId: project.id });
        if (!alive.current) return;
        onProject(res.data.project);
        setJob(res.data.job);
        setJobId(res.data.renderJobId);
      } catch (err) {
        if (!alive.current) return;
        const code = apiCode(err);
        setPhase(kind === 'preview' ? 'failed' : 'ready');
        const msg =
          code === 'ALLOWANCE_EXHAUSTED'
            ? `This program has used all ${allowance.limit} of its videos.`
            : apiMessage(err, 'The video could not be started. Your work has been saved. Try again.');
        setError(msg);
        if (kind === 'final') toast.error(msg);
      }
    },
    [project.id, onProject, allowance.limit]
  );

  useEffect(() => {
    if (phase === 'requesting') request('preview');
  }, [phase, request]);

  // Poll the active job.
  useEffect(() => {
    if (!jobId || (phase !== 'rendering' && phase !== 'publishing')) return undefined;
    let stopped = false;
    let timer: ReturnType<typeof setInterval> | null = null;
    const startedAt = Date.now();
    let failingSince: number | null = null;

    // Ends the poll and hands the user somewhere to go: a preview drops into
    // the failed state, which offers "Try Again"; a save returns to the ready
    // state, where the save button is live again.
    const giveUp = (message: string) => {
      if (stopped || !alive.current) return;
      stopped = true;
      if (timer) clearInterval(timer);
      const wasPublishing = phase === 'publishing';
      setPhase(wasPublishing ? 'ready' : 'failed');
      setError(message);
      if (wasPublishing) toast.error(message);
    };

    const tick = async () => {
      if (Date.now() - startedAt >= POLL_DEADLINE_MS) {
        giveUp("This is taking longer than it should and we've stopped waiting. Your work has been saved. Try again.");
        return;
      }
      try {
        const res: any = await API.getVideoRenderJob({ renderJobId: jobId });
        if (stopped || !alive.current) return;
        failingSince = null;
        const j = res.data as RenderJob;
        setJob(j);
        if (isTerminalJob(j.status)) {
          const fresh: any = await API.getVideoProject({ projectId: project.id });
          if (stopped || !alive.current) return;
          onProject(fresh.data);
          if (j.status === 'complete' && j.promoted) {
            if (j.type === 'final') {
              setPhase('saved');
              toast.success('Video saved to the lesson');
              onSaved();
            } else {
              setPhase('ready');
            }
          } else {
            setPhase(j.type === 'final' ? 'ready' : 'failed');
            setError(
              j.errorMessage ||
                (j.promoted ? null : 'The video changed while this preview was rendering. Generate a new preview.')
            );
          }
        }
      } catch {
        // Transient: the next tick retries. Unbroken failures past the grace
        // period mean nobody is coming back with an answer.
        if (stopped || !alive.current) return;
        const now = Date.now();
        if (failingSince === null) failingSince = now;
        else if (now - failingSince >= POLL_ERROR_GRACE_MS) {
          giveUp('We lost contact while the video was being made. Your work has been saved. Try again.');
        }
      }
    };
    tick();
    timer = setInterval(tick, 2500);
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
    };
  }, [jobId, phase, project.id, onProject, onSaved]);

  const styleName = MODE_LABEL[project.mode];
  const previewBackLabel = MODE_NOUN[project.mode];

  if (phase === 'rendering' || phase === 'publishing' || phase === 'requesting') {
    const publishing = phase === 'publishing';
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        data-testid={publishing ? 'publishing' : 'rendering'}
        aria-live="polite"
      >
        <Box>
          <Typography sx={{ fontFamily: vb.font, fontSize: 20, fontWeight: 800, color: vb.text }}>
            {publishing ? 'Saving video...' : 'Creating your preview...'}
          </Typography>
          <Typography sx={{ fontFamily: vb.font, fontSize: 13.5, color: vb.textSecondary, mt: 0.5 }}>
            {publishing
              ? 'Rendering the final version and attaching it to the lesson.'
              : 'Elliott is recording the narration and building the video. This usually takes a couple of minutes.'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            p: 2,
            borderRadius: '12px',
            border: `1px solid ${vb.border}`,
            background: vb.page
          }}
        >
          {STAGES.map((s) => {
            const done = s.done(job);
            const active = !done && s.active(job);
            return (
              <Box key={s.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {done ? (
                  <CheckCircleIcon sx={{ fontSize: 18, color: vb.success }} />
                ) : active ? (
                  <Box
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      border: `3px solid ${vb.brand}`,
                      borderTopColor: 'transparent',
                      animation: 'vbSpin 0.9s linear infinite',
                      '@keyframes vbSpin': { to: { transform: 'rotate(360deg)' } }
                    }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: vb.textTertiary }} />
                )}
                <Typography
                  sx={{
                    fontFamily: vb.font,
                    fontSize: 14,
                    fontWeight: active ? 700 : 500,
                    color: done || active ? vb.text : vb.textTertiary
                  }}
                >
                  {s.label}
                  {active && s.key === 'render' && job
                    ? ` · ${Math.max(0, Math.round(((job.progress - 30) / 55) * 100))}%`
                    : ''}
                </Typography>
              </Box>
            );
          })}
          <Typography
            sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary, mt: 0.5 }}
            data-testid="job-progress"
          >
            {job ? `${job.progressMessage} · ${job.progress}%` : 'Starting...'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={onBackground} sx={pillButtonSx()} data-testid="continue-background">
            Continue in Background, We&apos;ll Notify You
          </Button>
        </Box>
      </Box>
    );
  }

  if (phase === 'failed') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="render-failed">
        <Typography sx={{ fontFamily: vb.font, fontSize: 20, fontWeight: 800, color: vb.text }}>
          We couldn&apos;t generate the preview
        </Typography>
        <Typography role="alert" sx={{ fontFamily: vb.font, fontSize: 14, color: vb.textSecondary }}>
          {error || 'Your work has been saved. Try again.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button onClick={onKeepEditing} sx={pillButtonSx()}>
            Back to {previewBackLabel}
          </Button>
          <Button onClick={() => setPhase('requesting')} sx={pillButtonSx(true)} data-testid="retry-preview">
            Try Again
          </Button>
        </Box>
      </Box>
    );
  }

  const leftAfter = Math.max(0, allowance.remaining - 1);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="preview-ready">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontFamily: vb.font, fontSize: 18, fontWeight: 800, color: vb.text }}>
          Video preview · {lessonName}
        </Typography>
        <Box
          sx={{
            fontFamily: vb.font,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.1em',
            px: 0.7,
            py: 0.25,
            borderRadius: '4px',
            background: vb.border,
            color: vb.textSecondary
          }}
        >
          {styleName.toUpperCase()}
        </Box>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.textTertiary }}>
          Watch it, go back to edit if needed, save when it&apos;s right
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'relative',
          borderRadius: '12px',
          overflow: 'hidden',
          background: '#000',
          border: `1px solid ${vb.border}`
        }}
      >
        <Box
          component="video"
          key={project.previewUrl ?? ''}
          controls
          preload="metadata"
          poster={project.previewThumbnailUrl ?? undefined}
          src={project.previewUrl ?? undefined}
          aria-label="Video preview"
          data-testid="preview-player"
          sx={{ display: 'block', width: '100%', aspectRatio: '16 / 9' }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 12,
            px: 1,
            py: 0.35,
            borderRadius: '6px',
            background: 'rgba(0,0,0,0.6)',
            color: vb.text,
            fontFamily: vb.font,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.08em'
          }}
        >
          PREVIEW · {formatClock(project.previewDurationSeconds ?? 0)}
        </Box>
      </Box>
      {error && (
        <Typography role="alert" sx={{ fontFamily: vb.font, fontSize: 12.5, color: vb.danger }}>
          {error}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          pt: 1.5,
          borderTop: `1px solid ${vb.border}`
        }}
      >
        <Typography sx={{ fontFamily: vb.font, fontSize: 13, color: vb.textSecondary }}>
          Watch it through. Need a change?
        </Typography>
        <Button onClick={onKeepEditing} sx={pillButtonSx()} data-testid="keep-editing">
          Keep Editing{' '}
          <Box component="span" sx={{ color: vb.textTertiary, ml: 0.5 }}>
            · back to {previewBackLabel}
          </Box>
        </Button>
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontFamily: vb.font, fontSize: 12, color: vb.textTertiary }} data-testid="allowance-copy">
          Uses 1 course video · {leftAfter} left after
        </Typography>
        <Button
          onClick={() => {
            request('final');
          }}
          disabled={allowance.remaining <= 0}
          sx={pillButtonSx(true)}
          data-testid="save-video"
        >
          Save Video
        </Button>
      </Box>
    </Box>
  );
};

export default PreviewStep;
